import React, { useState, useEffect } from 'react';
import { Button, Card, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField } from '@material-ui/core';
import { MetersVService, ServiceAddressesVService } from '../../services/V2/gozer';
import { dateToLocal, dateToUTC, handleErrorToast } from '../shared/Functions';
import { Autocomplete } from '@material-ui/lab';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { ReaderVService, ReadsVService, RoutesVService } from '../../services/V2/aurascope';
import { RouteEditor } from './RouteEditor';
import { CSVReader } from 'react-papaparse';
import { CSVLink } from 'react-csv';
import { NewMeterModal } from './NewMeterModal';

export const RouteList = ({ districtId, operatorId, routeId, setShow, loading, setLoading, endpoint, defaultDisabled, failCallback, isAdd }) => {
    const [disabled, setDisabled] = useState<boolean>(true);
    const [serviceAddresses, setServiceAddresses] = useState<any[]>([]);
    const [serviceAddress, setServiceAddress] = useState<any>();
    const [meters, setMeters] = useState<any[]>([]);
    const [users, setUsers] = useState<any[]>([]);
    const [user, setUser] = useState<any>();
    const [routes, setRoutes] = useState<any[]>([]);
    const [formData, setFormData] = useState<any>();
    const [route, setRoute] = useState<any>();
    const [routeIndex, setRouteIndex] = useState<number>();
    const [showEdit, setShowEdit] = useState<boolean>(false);
    const [cancelWarning, setCancelWarning] = useState<boolean>(false);
    const [showNewMeter, setShowNewMeter] = useState<boolean>(false);

    useEffect(() => {
        if(routeId) {
            RoutesVService.routesVGetById(routeId)
                .then(r => {
                    setFormData(r);
                    if(r.route?.meters){
                        setRoutes(r.route?.meters.map(x => {
                            return {
                                ...x,
                                prevReadAt: x.prevReadAt ? dateToLocal(x.prevReadAt) : x.prevReadAt
                            }
                        }))
                    } else setRoutes([])
                })
                .catch(err => failCallback(handleErrorToast(err)))
        } else {
            setRoutes([])
            setFormData({})
        }
    }, [routeId])// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if(districtId) {
            ServiceAddressesVService.serviceAddressesVGetAll({
                pageNumber: 1,
                orderBy: "City",
                orderByDesc: true,
                filters: [{
                    property: "DistrictId",
                    value: districtId
                }]
            })
                .then((x: any) => setServiceAddresses(x?.items))
                .catch(err => failCallback(handleErrorToast(err)))
            ReaderVService.readerVGetAll(operatorId)
                .then(u => setUsers(u.sort((a,b) => a.name?.localeCompare(b?.name ?? "") ?? 0)))
                .catch(err => failCallback(handleErrorToast(err)))
        }
    }, [districtId])// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if(formData && users && !user) {
            setUser(users.filter(x => x.id === formData.userId)[0]);
        }
    }, [formData, users])// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setDisabled(defaultDisabled)
    }, [defaultDisabled])// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if(serviceAddress){
            MetersVService.metersVGetAll(serviceAddress.id, {
                pageNumber: 1,
                orderBy: "MeterNumber",
                orderByDesc: true,
                filters: [{
                    property: "ServiceAddressId",
                    value: serviceAddress.id
                }]
            })
                .then((m: any) => setMeters(m?.items))
                .catch(err => failCallback(handleErrorToast(err)))
        } else setMeters([]);
    }, [serviceAddress])// eslint-disable-line react-hooks/exhaustive-deps

    const getListStyle = isDraggingOver => ({
        background: isDraggingOver ? "lightblue" : "lightgrey",
        padding: 8,
        width: "100%",
        height:"75vh",
        overflow:"auto"
    });

    const onDragEnd = result => {
        // dropped outside the list
        if (!result.destination) {
          return;
        }
        const items = reorder(
            routes,
            result.source.index,
            result.destination.index
        );
        setRoutes(items);
    }

    const reorder = (list, startIndex, endIndex) => {
        setCancelWarning(true)
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        return result;
    };

    const getItemStyle = (isDragging, draggableStyle) => ({
        // some basic styles to make the items look a bit nicer
        userSelect: "none",
        padding: 16,
        margin: `0 0 8px 0`,
        // change background colour if dragging
        background: isDragging ? "lightgreen" : "white",
        // styles we need to apply on draggables
        ...draggableStyle
    });

    const handleChange = event => {
        let name = event.target.name;
        let value = event.target.value;
        if (event.target.type === "checkbox") {
            value = event.target.checked;
        }
        setFormData({
            ...formData,
            [name]: value
        })
    }

    const handleSave = () => {
        let saveableMeters = Array.from(routes)

        let saveableRoute = {
            ...formData,
            districtId: districtId,
            userId: user?.id,
            route: {
                meters: saveableMeters.map(x => {
                    let date = x.prevReadAt;
                    if(date){
                        date = dateToUTC(date)
                    }
                    return {
                        ...x,
                        prevReadAt: date
                    }
                })
            }
        }
        if(routeId) {
            endpoint(routeId, saveableRoute)
                .then(x => {
                    setLoading(!loading)
                    setShow(false)
                })
                .catch(err => failCallback(handleErrorToast(err)))
        } else {
            endpoint(saveableRoute)
                .then(x => {
                    setLoading(!loading)
                    setShow(false)
                })
                .catch(err => failCallback(handleErrorToast(err)))
        }
    }

    const removeRoute = index => {
        setCancelWarning(true)
        let r = Array.from(routes);
        r.splice(index, 1)
        setRoutes(r)
    }

    const setActiveState = () => {
        let confirm = window.confirm("Are you sure you want to do this?");

        if (confirm === true) {
            let newActiveState = formData.isActive ? false : true;

            let saveableRoute = {
                ...formData,
                isActive: newActiveState
            };

            RoutesVService.routesVUpdate(routeId, saveableRoute)
                .then(x => {
                    setLoading(!loading)
                    setShow(false)
                })
                .catch(err => failCallback(handleErrorToast(err)))
        }
    };

    const setDelete = () => {
        let confirm = window.confirm("Are you sure you want to do this?");

        if (confirm === true) {
            RoutesVService.routesVDelete(routeId)
                .then(x => {
                    setLoading(!loading)
                    setShow(false)
                })
                .catch(err => failCallback(handleErrorToast(err)))
        }
    };

    const editRoute = (r, i) => {
        setCancelWarning(true)
        setRoute(r)
        setRouteIndex(i)
        setShowEdit(true)
    }

    const handleCancel = () =>{
        if(cancelWarning){
            let confirm = window.confirm("The edits to the route haven't been saved. Are you sure you want to cancel?");
            if(!confirm) return;
        }
        setShow(false)
    }

    const csvUpload = (data) => {
        let newRoute: any[] = [];
        for(let i = 1; i < data.length; i++){
            if(data[i].data.length !== data[0].data.length) continue;
            let meter = {};
            data[i].data.forEach((x, index) => {
                if(data[0].data[index] === "prevReadAt")
                    meter[data[0].data[index]] = x === "" ? null : new Date(x);
                else
                    meter[data[0].data[index]] =  x === "" ? null : x;
            })
            newRoute.push(meter)
        }
        if(newRoute.some(x => (x.id !== null && x.id !== undefined) && routes.some(y => y.id === x.id)) ||
            newRoute.some((x, i) => (x.id !== null && x.id !== undefined) && newRoute.some((y, z) => i !== z && y.id === x.id)))
            failCallback("You have already added a meter with the same Id. This value should be unique.")
        else{
            let r = Array.from(routes)
            var r2 = r.concat(newRoute)
            setRoutes(r2);
        }
    }

    const addMeterToRoute = (o, isManuallyEntered) => {
        if(!o) return;
        if(!routes.some(r => o.id && r.id === o.id)){
            if(!isManuallyEntered) {
                ReadsVService.readsVGetAll({
                    pageNumber: 1,
                    orderBy: "ReadingDateTime",
                    orderByDesc: true,
                    filters: [{
                        property: "DistrictId",
                        value: districtId
                    },{ property: "MeterId",
                        value: o.id}]
                })
                    .then((reads: any) => {
                        let read = reads.items
                        setRoutes(routes.concat([
                            {
                                id: o.id,
                                districtId: districtId,
                                type: o.type,
                                meterNumber: o.meterNumber,
                                isActive: o.isActive,
                                location: o.location,
                                serviceAddressType: serviceAddress.type,
                                street: serviceAddress.street,
                                city: serviceAddress.city,
                                state: serviceAddress.state,
                                zip: serviceAddress.zip,
                                zip4: serviceAddress.zip4,
                                prevRead: read?.length > 0 ? read[0].read : null,
                                prevReadAt: read?.length > 0 ? dateToLocal(read[0].readingDateTime) : null
                            }
                        ]))
                    })
                    .catch(err => {
                        setRoutes(routes.concat([
                            {
                                id: o.id,
                                districtId: districtId,
                                type: o.type,
                                meterNumber: o.meterNumber,
                                isActive: o.isActive,
                                location: o.location,
                                serviceAddressType: serviceAddress.type,
                                street: serviceAddress.street,
                                city: serviceAddress.city,
                                state: serviceAddress.state,
                                zip: serviceAddress.zip,
                                zip4: serviceAddress.zip4
                            }
                        ]))
                    })
            } else {
                setRoutes(routes.concat([{
                    ...o,
                    prevReadAt: o.prevReadAt ? new Date(o.prevReadAt) : null
                }]))
            }
        } else failCallback("You have already picked this meter.")
    }

    if(!formData) return null;

    return (
        <Dialog
            fullWidth
            maxWidth="md"
            open={true}
            onClose={() => setShow(false)}
            aria-labelledby="form-dialog-title"
        >
            {showEdit &&
                <RouteEditor setShow={setShowEdit} route={route} routeList={routes} setRouteList={setRoutes} index={routeIndex} />
            }
            {showNewMeter &&
                <NewMeterModal setShow={setShowNewMeter} districtId={districtId} saveCallback={addMeterToRoute} />
            }
            <DialogTitle id="form-dialog-title">Route Builder</DialogTitle>
            <DialogContent>
        <div className="row col-12">
            <Grid item xs={7}
                container={true}
                alignContent="flex-start">
                <div className="row col-12">
                    <Grid item xs={6}
                        container={true}
                        alignContent="flex-start">
                         {users.length > 0 &&
                           <Autocomplete
                                disabled={disabled}
                                value={user || { value: "", name: ""}}
                                id="UserId"
                                options={users}
                                getOptionLabel={(option) => option?.value ?? `${option.name}`}
                                renderOption={(option, { selected }) => (
                                    <div style={{ width: "100%", textAlign:"center" }}>{option?.value ?? `${option.name}`}</div>
                                )}
                                style={{ width: 250,paddingRight: "10px", color: "#fff" }}
                                renderInput={(params) => <TextField {...params} label="User" variant="outlined" />}
                                getOptionSelected={(o, v) => o.id === v.id}
                                onChange={(e, o) => {
                                    setUser(o)
                                }}
                                /> 
                        } 
                    </Grid>
                    <Grid item xs={6} />
                    {!disabled && serviceAddresses &&
                        <Grid item xs={6}
                            container={true}
                            alignContent="flex-start">
                            <Autocomplete
                                value={serviceAddress}
                                id="ServiceAddress"
                                options={serviceAddresses}
                                getOptionLabel={(option) => option?.value ?? `${option?.city ?? ""}, ${option?.street ?? ""}(${option?.meters?.length ?? 0})`}
                                renderOption={(option, { selected }) => (
                                    <div style={{ width: "100%", textAlign:"center" }}>{option?.value ?? `${option?.city ?? ""}, ${option?.street ?? ""}(${option?.meters?.length ?? 0})`}</div>
                                )}
                                style={{ width: 250, paddingRight: "10px", paddingTop: "10px", color: "#fff" }}
                                renderInput={(params) => <TextField {...params} label="Service Address" variant="outlined" />}
                                getOptionSelected={(o, v) => o.id === v.id}
                                onChange={(e, o) => {
                                    setServiceAddress(o)
                                }}
                            />
                        </Grid>
                    }
                     {!disabled && meters &&
                        <Grid item xs={6}
                            container={true}
                            alignContent="flex-start">
                            <Autocomplete
                                value={{ value: ""}}
                                id="Meter"
                                options={meters}
                                getOptionLabel={(option) => option?.value ?? `${option?.meterNumber ?? "no number"} - ${option?.type ?? ""}`}
                                renderOption={(option, { selected }) => (
                                    <div style={{ width: "100%", textAlign:"center" }}>{option?.value ?? `${option?.meterNumber ?? "no number"} - ${option?.type ?? ""}`}</div>
                                )}
                                style={{ width: 250, paddingRight: "10px", paddingTop: "10px", color: "#fff" }}
                                renderInput={(params) => <TextField {...params} label="Meter" variant="outlined" />}
                                getOptionSelected={(o, v) => o.id === v.id}
                                onChange={(e, o) => addMeterToRoute(o, false)}
                            />
                        </Grid>
                    } 
                    <TextField
                        disabled={disabled}
                        onChange={handleChange}
                        required={false}
                        margin="normal"
                        name="name"
                        label="Name"
                        type="text"
                        value={formData === undefined ? null : formData.name}
                        fullWidth
                    />
                </div>
                {!disabled &&
                    <Button
                        onClick={() => setShowNewMeter(true)}
                        color="primary">Add New Meter</Button>
                }
                {isAdd &&
                    <div>
                        <CSVReader
                            onDrop={csvUpload}
                            onError={(a,b,c,d) => console.log(a,b,c,d)}
                            addRemoveButton
                            >
                            <span>Drop CSV file here or click to upload a Route.</span>
                        </CSVReader>
                    </div>
                }
            </Grid>
            <Grid item xs={5}
                container={true}
                alignContent="flex-start">
                <DragDropContext
                    onDragEnd={onDragEnd}>
                    <Droppable droppableId="droppable">
                    {(provided, snapshot) => (
                        <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        style={getListStyle(snapshot.isDraggingOver)}
                        >
                        {routes.map((item, index) => (
                            <Draggable
                                key={`${item.id}-${index} `}
                                draggableId={`${item.id}-${index} `}
                                index={index}
                                isDragDisabled={disabled}
                                >
                            {(provided, snapshot) => (
                                <Card
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                style={getItemStyle(
                                    snapshot.isDragging,
                                    provided.draggableProps.style
                                )}
                                >
                                    <span style={{float:"left"}}>{index+1}</span>
                                    <div>
                                        <div>{item?.id}</div>
                                        <div>{item?.customerName}</div>
                                        <div>{item?.street ?? ""}</div>
                                        <div>{item?.city ?? ""}, {item?.state} {item?.zip}</div>
                                        <div>{item?.location}</div>
                                        <div>{item?.meterNumber ?? "no number"} - {item?.type}</div>
                                        <div>{`Prev. Read: ${item?.prevRead ?? "N/A"} ${item?.prevReadAt ? `@  ${item.prevReadAt.toLocaleDateString()}`: ""}`}</div>
                                        <div>High/Low Read: {item?.highReadThreshold ?? "N/A"} / {item?.lowReadThreshold ?? "N/A"}</div>
                                    </div>
                                    {!disabled &&
                                        <>
                                            <Button color="secondary" onClick={() => removeRoute(index)}>Remove</Button>
                                            <Button onClick={() => editRoute(item, index)}>Edit</Button>
                                        </>
                                    }
                                </Card>
                            )}
                            </Draggable>
                        ))}
                        {provided.placeholder}
                        </div>
                    )}
                    </Droppable>
                </DragDropContext>
            </Grid>
        </div>
        </DialogContent>
            <DialogActions>
                <Button>
                    <CSVLink filename={`${formData.name}-export.csv`} data={routes} className="remove-decorations">download route</CSVLink>
                </Button>
                <Button onClick={() => handleCancel()}>
                    Cancel
                </Button>
                {!disabled && routeId &&
                    <>
                        <Button
                            color="secondary"
                            onClick={setActiveState}>
                            {formData.isActive ? "Deactivate" : "Activate"}
                        </Button>
                        <Button
                            color="secondary"
                            onClick={setDelete}>
                            Delete
                        </Button>
                    </>
                }
                {!disabled &&
                    <Button
                        onClick={handleSave}
                        color="primary">
                        Save
                    </Button>
                }
                {disabled &&
                    <Button onClick={() => setDisabled(false)}>
                        Edit
                    </Button>
                }
            </DialogActions>
        </Dialog>
    );
}